package us.laantech.laanmanage.feature.store.pos.ui

import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.ui.graphics.Color
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import us.laantech.common.dto.company.CompanyBranch
import us.laantech.common.dto.company.Department
import us.laantech.common.dto.PosConfigDTO
import us.laantech.common.dto.RetailProductDTO
import us.laantech.common.api.KtorClient


enum class PosButtonFunction {
    Department,
    Product,
    Amount,
    None
}

enum class PosButtonColors(val color: Color) {
    Default(Color.Unspecified),
    Blue(Color.Blue),
    Green(Color.Green),
    Red(Color.Red),
    Yellow(Color.Yellow),
    Purple(Color.Magenta),
    Gray(Color.Gray),
    Black(Color.Black),
    White(Color.White),
    Light(Color.LightGray),
    Dark(Color.DarkGray),
}

data class PosEditorUiState(
    val branches: List<CompanyBranch> = emptyList(),
    val selectedBranch: CompanyBranch? = null,

    val receiptTitle: List<String> = listOf(),
    val receiptHeaderLines: List<String> = listOf(),
    val receiptFooterLines: List<String> = listOf(),

    val posConfigs: List<PosConfigDTO> = listOf(),
    val selectedPosConfig: PosConfigDTO? = null,

    // Button Config Modal
    val selectedButtonSection: String = "user_defined",
    val selectedButtonIndex: Int = -1,
    val showButtonConfigModal: Boolean = false,
    val buttonOptions: List<String> = PosButtonFunction.entries.map { it.name },
    val selectedButtonFunction: Int = 2,

    val posButtonColors: List<String> = PosButtonColors.entries.map { it.name },
    val selectedButtonColor: String = PosButtonColors.Blue.name,
    val selectedDepartment: Department? = null,
    val selectedProduct: RetailProductDTO? = null,

    // if Amount
    val labelTextFieldState: TextFieldState = TextFieldState(""),
    val isTaxable: Boolean = true,
    val canUseEBT: Boolean = false,
    val amountTextFieldState: TextFieldState = TextFieldState(""),
    val isFixedAmount: Boolean = false,


    )

class PosEditorViewModel(
) : ViewModel() {

    private val _uiState = MutableStateFlow(PosEditorUiState())
    val uiState = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client
    val scope = CoroutineScope(Dispatchers.Default)

    fun loadPosConfigs() {
        scope.launch {
            val response = ktClient.get("/backoffice/store-management/pos/list")

            val posConfigs = response.body<List<PosConfigDTO>>()

            if (response.status.isSuccess()) {
                _uiState.update { it.copy(posConfigs = posConfigs) }
            }
        }
    }


    fun selectPosConfig(posConfig: PosConfigDTO) {
        _uiState.update {
            it.copy(selectedPosConfig = posConfig)
        }
    }

    fun configModalToggleIsTaxable() {
        _uiState.update { it.copy(isTaxable = !it.isTaxable) }
    }

    fun configModalToggleCanUseEBT() {
        _uiState.update { it.copy(canUseEBT = !it.canUseEBT) }
    }

    fun configModalToggleIsFixedAmount() {
        _uiState.update { it.copy(isFixedAmount = !it.isFixedAmount) }
    }

    fun selectButtonColor(colorName: String) {
        _uiState.update { it.copy(selectedButtonColor = colorName) }
    }

    fun selectButtonFunction(index: Int) {
        _uiState.update { it.copy(selectedButtonFunction = index) }
    }

    fun savePosConfig() {
        viewModelScope.launch {
            val posConfig = uiState.value.selectedPosConfig ?: return@launch

            ktClient.put("/backoffice/store-management/pos/update") {
                contentType(ContentType.Application.Json)
                setBody(posConfig)
            }
        }
    }

    fun loadBranches() {
        scope.launch {
            val response = ktClient.get("/backoffice/store-management/list")
            val branches = response.body<List<CompanyBranch>>()
            _uiState.update { it.copy(branches = branches) }
        }
    }

    fun selectBranch(branch: CompanyBranch) {
        _uiState.update { it.copy(selectedBranch = branch) }
    }

}